/**
 * Role Access Utilities
 * Shared capability checks by Strapi role type.
 */

import {
  type RoleType,
  ADMIN_PORTAL_ROLE_TYPES,
  SYSTEM_USER_ROLE_TYPES,
  VISITOR_MANAGE_ROLE_TYPES,
  FINANCE_ADMIN_ALLOWED_ROUTE_PREFIXES,
  EXECUTIVE_ALLOWED_ROUTE_PREFIXES,
  HEAD_OF_SALES_RESTRICTED_ROUTE_PREFIXES,
  HEAD_OF_SALES_ALLOWED_ORGANIZATION_ROUTE_PREFIXES,
  HEAD_OF_SALES_ALLOWED_LOCATIONS_ROUTE_PREFIXES,
  HEAD_OF_SALES_ALLOWED_GATE_OPS_ROUTE_PREFIXES,
  SALES_MANAGER_RESTRICTED_ROUTE_PREFIXES,
  SALES_MANAGER_ALLOWED_SYSTEM_ROUTE_PREFIXES,
  SALES_OFFICE_USER_RESTRICTED_ROUTE_PREFIXES,
  ADMIN_ROLE_RESTRICTED_ROUTES,
  IT_ROLE_RESTRICTED_ROUTES,
  ROLE_TYPES,
  GATE_FACTORY_SELECTOR_DENIED_ROLE_TYPES,
  GATE_PORTAL_EMPLOYEE_ALLOWED_ROUTES,
  GATE_PORTAL_SERVICES_MANAGER_ALLOWED_ROUTES,
  GATE_PORTAL_SECURITY_GUARD_ALLOWED_ROUTES,
  GATE_PORTAL_SECURITY_MANAGER_ALLOWED_ROUTES,
  GATE_PORTAL_VISITOR_APPROVAL_ADMIN_ALLOWED_ROUTES,
  GATE_PORTAL_SCALE_ALLOWED_ROUTES,
  GATE_PORTAL_PACKHOUSE_MANAGER_ALLOWED_ROUTES,
  GATE_PORTAL_SCM_ADMIN_ALLOWED_ROUTES,
  KIOSK_PORTAL_ALLOWED_ROLES,
} from './role-constants';

export function isSalesOfficeUserRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SALES_OFFICE_USER;
}

export function canAccessAdminPortal(roleType: RoleType): boolean {
  return ADMIN_PORTAL_ROLE_TYPES.includes(roleType as any);
}

export function canManageSystemUsers(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SUPER_ADMIN || roleType === ROLE_TYPES.ADMIN;
}

export function canControlGates(roleType: RoleType): boolean {
  return !isSalesOfficeUserRole(roleType);
}

export function canManageOrganizationData(roleType: RoleType): boolean {
  return (
    roleType !== ROLE_TYPES.FINANCE_ADMIN &&
    roleType !== ROLE_TYPES.HEAD_OF_SALES &&
    roleType !== ROLE_TYPES.IT
  );
}

export function isSystemUserRole(roleType: RoleType): boolean {
  return SYSTEM_USER_ROLE_TYPES.includes(roleType as any);
}

export function canAccessAdminRoute(
  roleType: RoleType,
  pathname: string,
): boolean {
  if (!pathname) return false;

  if (roleType === ROLE_TYPES.ADMIN) {
    return !ADMIN_ROLE_RESTRICTED_ROUTES.some(
      (route) => pathname === route || pathname.startsWith(`${route}/`),
    );
  }

  if (roleType === ROLE_TYPES.FINANCE_ADMIN) {
    return FINANCE_ADMIN_ALLOWED_ROUTE_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );
  }

  if (roleType === ROLE_TYPES.EXECUTIVE) {
    return EXECUTIVE_ALLOWED_ROUTE_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );
  }

  if (roleType === ROLE_TYPES.IT) {
    return !IT_ROLE_RESTRICTED_ROUTES.some(
      (route) => pathname === route || pathname.startsWith(`${route}/`),
    );
  }

  if (roleType === ROLE_TYPES.HEAD_OF_SALES) {
    if (pathname.startsWith('/gate-ops')) {
      return HEAD_OF_SALES_ALLOWED_GATE_OPS_ROUTE_PREFIXES.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
      );
    }

    if (
      HEAD_OF_SALES_RESTRICTED_ROUTE_PREFIXES.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
      )
    ) {
      return false;
    }

    if (pathname.startsWith('/organization')) {
      return HEAD_OF_SALES_ALLOWED_ORGANIZATION_ROUTE_PREFIXES.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
      );
    }

    if (pathname.startsWith('/locations')) {
      return HEAD_OF_SALES_ALLOWED_LOCATIONS_ROUTE_PREFIXES.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
      );
    }

    return true;
  }

  if (roleType === ROLE_TYPES.SALES_OFFICE_USER) {
    return !SALES_OFFICE_USER_RESTRICTED_ROUTE_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );
  }

  if (roleType === ROLE_TYPES.SALES_MANAGER) {
    if (
      SALES_MANAGER_RESTRICTED_ROUTE_PREFIXES.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
      )
    ) {
      return false;
    }

    if (pathname.startsWith('/system')) {
      return SALES_MANAGER_ALLOWED_SYSTEM_ROUTE_PREFIXES.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
      );
    }

    return true;
  }

  return true;
}

export function getDefaultAdminRouteForRole(roleType: RoleType): string {
  if (roleType === ROLE_TYPES.FINANCE_ADMIN) return '/finance/overview';
  if (roleType === ROLE_TYPES.EXECUTIVE) return '/';
  return '/';
}

/**
 * IT role has read-only access to every admin portal page — used by UI to
 * hide create/edit/delete controls without restricting route access.
 */
export function isReadOnlyAdminRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.IT;
}

/**
 * Roles allowed read-only access to the factory / city / region management
 * pages (which are otherwise restricted to system administrators). IT has it
 * platform-wide; sales_manager gets visibility into these location-config pages
 * without the ability to mutate them.
 */
export function isLocationConfigReadOnlyRole(roleType: RoleType): boolean {
  return (
    isReadOnlyAdminRole(roleType) ||
    roleType === ROLE_TYPES.SALES_MANAGER ||
    roleType === ROLE_TYPES.HEAD_OF_SALES
  );
}

export function canManageVisitors(roleType: RoleType): boolean {
  return VISITOR_MANAGE_ROLE_TYPES.includes(roleType as any);
}

export function canSelectGateFactory(roleType: RoleType): boolean {
  if (!roleType) return false;
  return !GATE_FACTORY_SELECTOR_DENIED_ROLE_TYPES.includes(roleType as any);
}

export function isEmployeeRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.EMPLOYEE;
}

export function isSafetyManagerRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SAFETY_MANAGER;
}

export function isServicesManagerRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SERVICES_MANAGER;
}

export function isSecurityGuardRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SECURITY_GUARD;
}

export function isSecurityManagerRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SECURITY_MANAGER;
}

export function isVisitorApprovalAdminRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.VISITOR_APPROVAL_ADMIN;
}

export function canEmployeeAccessRoute(pathname: string): boolean {
  if (!pathname) return false;
  return GATE_PORTAL_EMPLOYEE_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export function canServicesManagerAccessRoute(pathname: string): boolean {
  if (!pathname) return false;
  return GATE_PORTAL_SERVICES_MANAGER_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export function canSecurityGuardAccessRoute(pathname: string): boolean {
  if (!pathname) return false;
  return GATE_PORTAL_SECURITY_GUARD_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export function canSecurityManagerAccessRoute(pathname: string): boolean {
  if (!pathname) return false;
  return GATE_PORTAL_SECURITY_MANAGER_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export function canVisitorApprovalAdminAccessRoute(pathname: string): boolean {
  if (!pathname) return false;
  return GATE_PORTAL_VISITOR_APPROVAL_ADMIN_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export function isScaleOperatorRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SCALE_OPERATOR;
}

export function isScaleAdminRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SCALE_ADMIN;
}

export function isScaleRole(roleType: RoleType): boolean {
  return isScaleOperatorRole(roleType) || isScaleAdminRole(roleType);
}

export function canScaleRoleAccessRoute(pathname: string): boolean {
  if (!pathname) return false;
  return GATE_PORTAL_SCALE_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export function getDefaultGateRouteForRole(roleType: RoleType): string {
  if (isScaleRole(roleType)) return '/weighbridge-ops';
  if (isScmAdminRole(roleType)) return '/incoming-materials';
  return '/';
}

export function isScmAdminRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SCM_ADMIN;
}

export function canScmAdminAccessRoute(pathname: string): boolean {
  if (!pathname) return false;
  return GATE_PORTAL_SCM_ADMIN_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export function isPackhouseManagerRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.PACKHOUSE_MANAGER;
}

export function canPackhouseManagerAccessRoute(pathname: string): boolean {
  if (!pathname) return false;
  return GATE_PORTAL_PACKHOUSE_MANAGER_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export function canAccessKioskPortal(roleType: RoleType): boolean {
  return KIOSK_PORTAL_ALLOWED_ROLES.includes(roleType as any);
}
